import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
// import { Modal } from 'antd';

import Step from '@components/individualSteps';
import actions from '@flux/action';

import {
  quesModel,
  QUES_SECTIONS_SEQ,
  QUES_STATUS,
  SECTIONS_LENGTH,
  QUESTION_STATUS,
} from '@model/questionmaires';
import alert from '@components/alert';
import { getSessionStorage } from '@utils/windowUtil';

// const modal = Modal.info;

// const defaultModalProps = {
//   icon: null,
//   maskClosable: true,
//   okButtonProps: {
//     className: 'questionnaire-response-modal-ok',
//     size: 'large',
//   },
//   okText: 'review responses',
//   title: (
//     <div className="questionnaire-response-modal-title">
//       Questionnaire Complete
//     </div>
//   ),
//   content: (
//     <div className="questionnaire-response-modal-body">
//       <p>
//         Thank you for completing the Diagnostic Questionnaire. Please proceed to
//         the next section to review and submit your responses.
//       </p>
//       <p>
//         The completed Questionnaire will provide an initial assessment of levels
//         of complexity across five major sections: Strategy, Value, Enterprise,
//         People, Execution. This will help drive an understanding of levels of
//         effort and resources needed to successfully execute the integration.
//       </p>
//     </div>
//   ),
//   className: 'questionnaire-response-modal',
// };

// function useQuestionSteps(QSstepData) {
function useQuestionSteps(fetchFirstQuestion = true) {
  const { section, sequence } = useParams();
  const history = useHistory();
  const registerRef = useRef();
  const [currentAnswer, setCurrentAnswer] = useState(null);

  const {
    status,
    currentAttributes,
    isFetchLoading,
    isActionLoading,
    QSaccessData,
  } = useSelector(({ question }) => ({
    status: question.status,
    currentAttributes: question.currentQuesAttributes || {},
    isFetchLoading: question.isFetchLoading,
    isActionLoading: question.isActionLoading,
    QSaccessData: question.QSaccessData,
  }));

  const { quesDatas, max, min, isFirstSection, isLastSection, preLength } =
    useMemo(() => {
      const quesDatas = quesModel[section] || [];
      const optInSections = QUES_SECTIONS_SEQ.filter(
        (section) => !QSaccessData[section]?.disabled,
      );
      return {
        preLength: getPreLength(section),
        quesDatas,
        max: quesDatas.length - 1,
        min: 0,
        isFirstSection: optInSections.indexOf(section) === 0,
        isLastSection:
          optInSections.indexOf(section) === optInSections.length - 1,
      };
    }, [section, QSaccessData]);
  const [stepIndexs, setStep] = useState(() => {
    const currentStep = Number(getSessionStorage('currentStep'));
    window.SessionStorage.removeItem('currentStep');
    if (currentStep >= 0) {
      return {
        currentStep: 0 + currentStep,
        actualIndex: preLength + currentStep,
        initialSet: true,
      };
    }
    return {
      currentStep: 0,
      actualIndex: preLength,
      initialSet: true,
    };
  });
  const { currentStep, actualIndex, initialSet } = stepIndexs;

  const isFirst = isFirstSection && currentStep === min;
  const isLast = isLastSection && currentStep === max;

  const dispatch = useDispatch();

  async function submitQuestionAnswer(restParams, values) {
    return await dispatch(
      actions.submitQuestionAnswer({
        ...restParams,
        values: typeof values === 'function' ? values() : values,
        isLast,
        shouldAnswerRequest: values !== '',
      }),
    );
  }

  const syncData = async (params) => {
    const { actionType, ...restParams } = params;
    if (!needSyncAction(actionType)) {
      return { success: true };
    }
    const values =
      registerRef.current?.getValues || registerRef.current?.values;
    return await submitQuestionAnswer(restParams, values);
  };

  const changeSection = (section) => {
    history.push(`/questionnaires/${section}`);
  };

  const goToResponse = () => history.push(`/questionnaires/review-responses`);

  const nextStep = () => {
    if (currentStep === max) {
      setStep({
        currentStep: 0,
        actualIndex: preLength + max + 1,
      });
      changeSection(nextSection(section, QSaccessData));
    } else {
      const nextStep = Math.min(currentStep + 1, max);
      setStep({
        currentStep: nextStep,
        actualIndex: nextStep + preLength,
      });
    }
  };

  // const showModal = () => {
  //   modal({
  //     ...defaultModalProps,
  //     onOk: goToResponse,
  //   });
  // };

  const preStep = () => {
    if (currentStep === min) {
      const aPreSection = preSection(section, QSaccessData),
        newStepIndex = quesModel[aPreSection].length - 1;

      setStep({
        currentStep: newStepIndex,
        actualIndex: preLength - 1,
      });
      changeSection(aPreSection);
    } else {
      const newIndex = Math.max(currentStep - 1, min);

      setStep({
        currentStep: Math.max(currentStep - 1, min),
        actualIndex: newIndex + preLength,
      });
    }
  };

  const resetSection = async () => {
    const currentSectionSeq = QUES_SECTIONS_SEQ.indexOf(section) + 1;
    await dispatch(actions.resetSection(currentSectionSeq));

    if (currentStep === min) {
      dispatch(
        actions.changeTargetStepStatusAndFetch({
          stepIndex: currentStep,
          section,
          actualIndex,
        }),
      );
    } else {
      setStep({
        currentStep: min,
        actualIndex: preLength,
      });
    }
  };

  const handleActions = (actionType) => async () => {
    if (isActionLoading) {
      return;
    }

    let syncResult;
    try {
      syncResult = await syncData({
        index: actualIndex,
        actionType,
        section,
      });
    } catch (e) {
      return;
    }

    const { success } = syncResult || {};
    if (!success) {
      return;
    }

    switch (actionType) {
      case 'pre':
        !isFirst && preStep();
        break;

      case 'next':
        if (isLast) {
          goToResponse();
        } else {
          nextStep();
        }
        break;

      case 'later':
        !isLast && nextStep();
        break;

      case 'reset':
        resetSection();
        break;

      default:
        throw new Error('need action type!');
    }

    setCurrentAnswer(null);
  };
  const markNotApplicable = () => async () => {
    const allAreNotApplicableExceptOne =
      status[section].filter((e) => e === QUESTION_STATUS.NotApplicable)
        .length ===
      status[section].length - 1;
    const restAreAnswered =
      status[section].filter((e) =>
        [QUESTION_STATUS.Finish, QUESTION_STATUS.NotApplicable].includes(e),
      ).length >=
      status[section].length - 1;
    const currentMarkAsNotApplicable =
      currentAttributes.questionnaire?.status[actualIndex] !==
      QUESTION_STATUS.NotApplicable;
    const lastMarkAsNotApplicable =
      currentMarkAsNotApplicable && restAreAnswered;

    if (allAreNotApplicableExceptOne && currentMarkAsNotApplicable) {
      // if rest of the questions for current section are marked as "Not Applicable"
      alert.warning({
        content:
          'You cannot set all questions in this section as "Not Applicable',
      });
      return;
    }
    // GET API to mark the current question as 'Not Applicable'.
    await dispatch(
      actions.changeToNotApplicable({
        actualIndex,
      }),
    );
    // Refetch from BE to update the view, state change is handled in BE.
    dispatch(
      actions.changeTargetStepStatusAndFetch({
        // create a reducer similar to this
        stepIndex: currentStep,
        section,
        actualIndex,
      }),
    );
    if (lastMarkAsNotApplicable) {
      await submitQuestionAnswer(
        {
          index: actualIndex,
          actionType: 'notApplicable',
          section,
        },
        '',
      );
    }
  };
  const handleStepClick = ({ status }, index) => {
    if (!QUES_STATUS.includes(status) || QUES_STATUS.indexOf(status) === 0) {
      return;
    }

    setStep({
      currentStep: index,
      actualIndex: preLength + index,
    });
    setCurrentAnswer(null);
  };

  const hasSequenceInPath = window.location.pathname.search(/\/[0-9]+/) !== -1;

  useEffect(() => {
    const minIndex = getPreLength(section),
      maxIndex = preLength + max;
    if (
      actualIndex >= minIndex &&
      actualIndex <= maxIndex &&
      sequence === undefined
    ) {
      return;
    }

    const seqToAdd = parseInt(sequence || '0', 10);

    if (seqToAdd === 0 && hasSequenceInPath) {
      return;
    }

    setStep({
      currentStep: 0 + seqToAdd,
      actualIndex: minIndex + seqToAdd,
    });
  }, [section, sequence]);

  useEffect(() => {
    if (!fetchFirstQuestion && actualIndex === 0) {
      return;
    }
    if (initialSet && hasSequenceInPath) {
      return;
    }

    dispatch(
      actions.changeTargetStepStatusAndFetch({
        stepIndex: currentStep,
        section,
        actualIndex,
      }),
    );
  }, [actualIndex]);

  return {
    nextStepAndSave: handleActions('next'),
    preStepAndSave: handleActions('pre'),
    answerLater: handleActions('later'),
    resetSection: handleActions('reset'),
    markNotApplicable: markNotApplicable(),
    register: registerRef,
    sequence: actualIndex,
    quesData: quesDatas?.[currentStep] || {},
    currentSection: section,
    isFirst,
    isLast,
    answer: getDefaultAnswer(currentAttributes.answer),
    isFetchLoading,
    isActionLoading,
    allStatus: currentAttributes.questionnaire?.status,
    currentAnswer,
    setCurrentAnswer,
    step: (
      <Step
        data={transStatus(status[section], quesModel[section])}
        currentStep={currentStep + 1}
        click={handleStepClick}
      />
    ),
  };
}

export default useQuestionSteps;

const getDefaultAnswer = (answer) => {
  if (!answer || Object.keys(answer).length === 0) {
    return undefined;
  }

  return answer;
};

const needSyncAction = (actionType) => ['pre', 'next'].includes(actionType);

const nextSection = (currentSection, QSaccessData) => {
  const optInSections = QUES_SECTIONS_SEQ.filter(
    (section) => !QSaccessData[section]?.disabled,
  );

  const nextIndex = optInSections.indexOf(currentSection) + 1;

  return nextIndex && nextIndex < optInSections.length
    ? optInSections[nextIndex]
    : '';
};

const preSection = (currentSection, QSaccessData) => {
  const optInSections = QUES_SECTIONS_SEQ.filter(
    (section) => !QSaccessData[section]?.disabled,
  );
  const preIndex = optInSections.indexOf(currentSection) - 1;

  return preIndex > -1 ? optInSections[preIndex] : '';
};

const getPreLength = (currentSection) => {
  let result = 0;

  if (!QUES_SECTIONS_SEQ.includes(currentSection)) {
    return result;
  }

  for (const section of QUES_SECTIONS_SEQ) {
    if (currentSection === section) {
      break;
    }
    result = result + quesModel[section].length;
  }

  return result;
};

const transStatus = (status = [], questionText) =>
  status.map((item, index) => ({
    status: QUES_STATUS[item],
    tooltipText: questionText[index].content,
  }));

export const sortSectionStatus = (data = []) => {
  const dataTemp = [...data];

  return SECTIONS_LENGTH.reduce((acc, item, i) => {
    const currentSection = QUES_SECTIONS_SEQ[i];
    acc[currentSection] = dataTemp.splice(0, item);
    return acc;
  }, {});
};
